import { getStepPlacementParagraphPositions, hasOnlyPlacement, hasOnlyBoundaryPlacements } from "~/utils/self-assessment-placement";
import { Delta } from "@vueup/vue-quill";
import { createBannedWordMatcher, getBannedWordText, type BannedWordInstance } from "~/utils/banned-words";
import { SelfAssessmentStep } from "~/classes/models/self-assessment/self-assessment-step.model";
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";
import {
  analyzeDocumentBlocks,
  snapshotDocumentBlockInputs,
} from "~/composables/document-analysis/analyzeDocumentBlocks";
import {
  useAllBodyParagraphs,
  useAllParagraphs,
} from "~/composables/quill-selection/useAllParagraphs";
import { useAttributeInstances } from "~/composables/quill-selection/useAttributeInstances";
import { useParagraphSentences } from "~/composables/quill-selection/useParagraphSentences";
import { useSelfAssessmentStepValidation } from "~/composables/selfAssessmentValidation/useSelfAssessmentStepValidation";

export const useDocumentSelfAssessment = (documentId: string) =>
  defineStore(`/document/${documentId}/self-assessment`, () => {
    const submittedDocumentStore = useSubmittedDocumentStore(documentId);
    const { revision, submittedDocument } = storeToRefs(submittedDocumentStore);

    const documentChecklistStore = useDocumentChecklistStore(documentId);
    const { useAllCriteriaInstances, useChecklist, criteriaFromId } =
      storeToRefs(documentChecklistStore);

    const wordProcessorStore = useWordProcessorStore(documentId);

    const showBadge = ref(false);

    const quillDelta = ref(new Delta());
    const quillContentTimestamp = ref(Date.now());
    const onQuillTextChange = () => {
      quillContentTimestamp.value++;
    };

    const removeQuillListener = (instance: any) => {
      if (!instance?.off) return;
      instance.off("text-change", onQuillTextChange);
    };

    const addQuillListener = (instance: any) => {
      if (!instance?.on) return;
      instance.on("text-change", onQuillTextChange);
    };

    const setQuillEditor = () => {
      const quill = wordProcessorStore.getQuillInstance();

      removeQuillListener(quill);
      addQuillListener(quill);
    };

    const selfAssessmentConfiguration = ref<{
      steps: SelfAssessmentStep[];
    }>({
      steps: [],
    });

    const allSteps = computed(() => {
      return [...preCheckSteps.value, ...guidedReviewSteps.value];
    });

    const preCheckSteps = computed(() => {
      return selfAssessmentConfiguration.value.steps.filter(
        (step) => step.isPreCheckStep == true
      );
    });

    const guidedReviewSteps = computed(() => {
      return selfAssessmentConfiguration.value.steps.filter(
        (step) => step.isPreCheckStep != true && step.isBaseMessageStep != true
      );
    });

    const baseMessageSteps = computed(() => {
      return selfAssessmentConfiguration.value.steps.filter(
        (step) => step.isBaseMessageStep == true
      );
    });

    const isInitialized = ref(false);
    const generalError = ref<string | undefined>(undefined);

    const bannedWords = ref<string[]>([]);

    const initialize = async () => {
      if (isInitialized.value) return;

      bannedWords.value = [];

      const selfAssessmentSteps: SelfAssessmentStep[] = [];

      for (const criteria of useAllCriteriaInstances.value ?? []) {
        if (criteria.ignore == true) {
          continue;
        }

        if (criteria.checklistCriteriaId == undefined) {
          continue;
        }

        const checklistCriteria = documentChecklistStore.getCriteriaFromId(
          criteria.checklistCriteriaId!
        );

        if (checklistCriteria == undefined) {
          continue;
        }

        if (checklistCriteria.isBannedWordsCriteria == true) {
          if (checklistCriteria.evaluationMetricId == undefined) {
            continue;
          }

          const evaluationMetric =
            await useEvaluationMetrics().fetchEvaluationMetric(
              checklistCriteria.evaluationMetricId
            );

          const metricOverrides = evaluationMetric.overrides;
          const metricOverrideKeys = Object.keys(metricOverrides);

          let words =
            useChecklist.value?.overrides?.[metricOverrideKeys[0]] ?? [];

          if (words.length == 0) {
            words =
              evaluationMetric.criteria.find(
                (c) => (c.options.values ?? []).length > 0
              )?.options.values ?? [];
          }

          bannedWords.value.push(...words);
        }

        if (checklistCriteria.selfAssessmentSteps != undefined) {
          for (const step of checklistCriteria.selfAssessmentSteps) {
            step.checklistCriteria = checklistCriteria;
            step.criteriaInstance = criteria;
          }

          selfAssessmentSteps.push(...checklistCriteria.selfAssessmentSteps);
        }
      }

      selfAssessmentConfiguration.value.steps = selfAssessmentSteps;

      // For debugging
      // for (const step of selfAssessmentConfiguration.value.steps) {
      //   step.isComplete = true;
      // }

      if (submittedDocument.value?.state == DocumentState.studentCompleted) {
        for (const step of selfAssessmentConfiguration.value.steps) {
          step.isComplete = true;
          step.assessmentResults = true;
          step.errorMessage = undefined;
        }

        hasRunAssessment.value = true;
        setActiveStepIndex(selfAssessmentConfiguration.value.steps.length);
      } else {
        setActiveStepIndex(-2);
      }

      // if (submittedDocument.value?.studentGuidedReviewStepIndex != undefined) {
      //   // Set all the stepss up to this one as complete
      //   for (
      //     let i = 0;
      //     i < submittedDocument.value!.studentGuidedReviewStepIndex - 1;
      //     i++
      //   ) {
      //     selfAssessmentConfiguration.value.steps[i].isComplete = true;
      //     selfAssessmentConfiguration.value.steps[i].assessmentResults = true;
      //     selfAssessmentConfiguration.value.steps[i].errorMessage = undefined;
      //   }

      //   setActiveStepIndex(
      //     submittedDocument.value!.studentGuidedReviewStepIndex
      //   );
      // } else {
      //   setActiveStepIndex(-1);
      // }

      isInitialized.value = true;
    };

    const activeStepIndex = ref<number | undefined>(-2);

    const isRunningAssessment = computed(() => {
      return selfAssessmentConfiguration.value.steps.some(
        (step) => step.isAssessing == true
      );
    });

    const headerSteps = computed(() => {
      return selfAssessmentConfiguration.value.steps.filter(
        (step) => step.isHeader == true
      );
    });

    const allParagraphs = computed(() => {
      const quill = wordProcessorStore.getQuillInstance();
      if (!quill) return [];

      // Include timestamp to trigger recomputation when content changes
      quillContentTimestamp.value;
      return useAllParagraphs(quill);
    });

    const headerParagraphs = computed(() => {
      const quill = wordProcessorStore.getQuillInstance();
      if (!quill) return [];

      // Include timestamp to trigger recomputation when content changes
      quillContentTimestamp.value;
      var indexes = headerSteps.value
        .map((step) => {
          var relevantInstances = useAttributeInstances(
            quill,
            revision.value?.studentSubmissionOps ?? [],
            step.criteriaAttribute.attribute
          );
          return relevantInstances.map((instance) => {
            return instance.paragraphIndex;
          });
        })
        .flat();
      return [...new Set(indexes)];
    });

    const documentBlockAnalysis = computed(() => {
      // Re-run the analysis whenever Quill emits a text change.
      quillContentTimestamp.value;

      // Initial Review must only inspect the document's structure. Header
      // attributes are created by later student-identification steps and can
      // otherwise make this earlier step impossible to complete on return.
      const inputs = snapshotDocumentBlockInputs(allParagraphs.value);

      return analyzeDocumentBlocks(inputs);
    });

    const documentBlocks = computed(() => documentBlockAnalysis.value.blocks);
    const bodyBlocks = computed(() => documentBlockAnalysis.value.bodyBlocks);
    const detectedBodyParagraphCount = computed(
      () => documentBlockAnalysis.value.bodyParagraphCount
    );

    const bodyParagraphs = computed(() => {
      const quill = wordProcessorStore.getQuillInstance();
      if (!quill) return [];

      // Include timestamp to trigger recomputation when content changes
      quillContentTimestamp.value;
      return useAllBodyParagraphs(
        quill,
        useChecklist.value?.paragraphPositionsData ?? []
      );
    });

    const stepExpectedInstances = computed(() => {
      return (step: SelfAssessmentStep) => {
        if (
          hasOnlyBoundaryPlacements(step)
        ) {
          // For super intros and conclusion we don't want to multiple by the number of body paragraphs
          if (step.rquireExactInstancesCount == true) {
            return step.numExpectedInstances ?? 0;
          }
          // The number of body paragraphs is the total number of body paragraphs minus the introduction and conclusion paragraphs
          var filteredBodyParagraphCount = bodyParagraphs.value.length - 2;

          // We expect the number of instances to be at least the number of body paragraphs
          return filteredBodyParagraphCount * (step.numExpectedInstances ?? 0);
        }

        if (step.evaluationMethod == "instance") {
          const stepCriteriaInstance = useAllCriteriaInstances.value.find(
            (c) =>
              c.checklistCriteriaId ==
              step.criteriaInstance?.checklistCriteriaId
          );

          if (
            stepCriteriaInstance != undefined &&
            stepCriteriaInstance.maxPoints != undefined &&
            stepCriteriaInstance.maxPoints > 0
          ) {
            return (
              stepCriteriaInstance.maxPoints /
              stepCriteriaInstance.pointsPerInstance
            );
          }
        }

        if (step.evaluationMethod == "paragraph") {
          if (
            revision.value?.id == undefined ||
            step.criteriaInstance?.checklistSectionId == undefined ||
            step.criteriaInstance?.checklistCriteriaId == undefined
          ) {
            return 0;
          }

          const paragraphPositions = stepParagraphPositions.value(step);

          return paragraphPositions.length * (step.numExpectedInstances ?? 0);
        }

        return step.numExpectedInstances ?? 0;
      };
    });

    const stepParagraphPositions = computed(() => {
      return (step: SelfAssessmentStep) => {
        if (
          revision.value?.id == undefined ||
          step.criteriaInstance?.checklistSectionId == undefined ||
          step.criteriaInstance?.checklistCriteriaId == undefined
        ) {
          return [];
        }

        const evaluationCriteriaInstanceStore = useEvaluationCriteriaInstance(
          documentId,
          revision.value?.id,
          step.criteriaInstance?.checklistSectionId,
          step.criteriaInstance?.checklistCriteriaId,
          step.criteriaInstance
        )();

        const { criteriaParagraphPositions } = storeToRefs(
          evaluationCriteriaInstanceStore
        );

        return getStepPlacementParagraphPositions(step, criteriaParagraphPositions.value);
      };
    });

    const hasRunAssessment = ref(false);
    const runAssessment = async () => {
      generalError.value = undefined;
      if (submittedDocument.value == undefined) {
        return;
      }

      if (submittedDocument.value.submissionSeal == undefined) {
        submittedDocument.value!.numAssessmentAttempts =
          (submittedDocument.value!.numAssessmentAttempts ?? 0) + 1;
      }

      await useSubmittedDocumentStore(documentId).saveDocumentDebounced();

      activeStepIndex.value = undefined;

      await Promise.all(
        selfAssessmentConfiguration.value.steps.map(async (step) => {
          await assessStep(step);
        })
      );

      // Check if all the steps passed
      let allStepsPassed = selfAssessmentConfiguration.value.steps.every(
        (step) => step.assessmentResults == true
      );

      checkForBannedWords();
      if (bannedWords.value.length > 0) {
        if (bannedWordsInstances.value.length > 0) {
          generalError.value =
            "Oops! You used banned words. Look for words with a curvy orange line. Replace banned words with words that show strong image or feeling. ";
          allStepsPassed = false;
        }
      }

      hasRunAssessment.value = true;

      if (allStepsPassed == false) {
        // Set the active step index to the first step that did not pass
        const firstFailedStepIndex = allSteps.value.findIndex(
          (step) =>
            step.assessmentResults == false &&
            step.isPreCheckStep != true &&
            step.isBaseMessageStep != true
        );

        if (firstFailedStepIndex >= 0) {
          activeStepIndex.value = firstFailedStepIndex;
        }

        return;
      }

      if (
        submittedDocument.value?.enableSubmissionBadges == true &&
        allStepsPassed == true &&
        submittedDocument.value != undefined
      ) {
        var numAssessmentAttempts =
          submittedDocument.value!.numAssessmentAttempts ?? 0;

        if (numAssessmentAttempts <= 1) {
          submittedDocument.value!.submissionSeal = SubmittedDocumentBadge.gold;
        } else if (numAssessmentAttempts <= 4) {
          submittedDocument.value!.submissionSeal =
            SubmittedDocumentBadge.silver;
        } else {
          submittedDocument.value!.submissionSeal =
            SubmittedDocumentBadge.bronze;
        }

        showBadge.value = true;
      }

      await useSubmittedDocumentStore(documentId).saveDocumentDebounced();
    };

    const assessStep = async (step: SelfAssessmentStep) => {
      step.errorMessage = undefined;
      step.assessmentResults = undefined;

      const quill = wordProcessorStore.getQuillInstance();

      if (quill == undefined) {
        return false;
      }

      let response = false;

      try {
        response = await useSelfAssessmentStepValidation({
          documentId,
          step,
          relevantInstances: stepRelevantInstances.value(step) ?? [],
          quill: quill,
        });
      } catch (error: any) {
        switch (error?.message) {
          case "not-enough-instances":
            step.errorMessage =
              step.errorMessageMap?.["not-enough-instances"] &&
              step.errorMessageMap?.["not-enough-instances"].length > 0
                ? step.errorMessageMap?.["not-enough-instances"]
                : "Oops! You are missing the required number of instances.";
            step.assessmentResults = false;
            break;
          case "too-many-instances-error":
            console.log("too-many-instances-error", step);
            step.errorMessage =
              step.errorMessageMap?.["too-many-instances-error"] &&
              step.errorMessageMap?.["too-many-instances-error"].length > 0
                ? step.errorMessageMap?.["too-many-instances-error"]
                : "Oops! You have too many selected.";
            step.assessmentResults = false;
            break;
          case "not-passed":
            step.errorMessage =
              step.errorMessageMap?.["not-passed"] &&
              step.errorMessageMap?.["not-passed"].length > 0
                ? step.errorMessageMap?.["not-passed"]
                : "Oops! You did not pass the assessment.";
            "Oops! You did not pass the assessment.";
            step.assessmentResults = false;
            break;
          case "formatting-error":
            step.errorMessage =
              step.errorMessageMap?.["formatting-error"] &&
              step.errorMessageMap?.["formatting-error"].length > 0
                ? step.errorMessageMap?.["formatting-error"]
                : "Oops! You did not pass the assessment.";
            "Oops! You did not pass the assessment.";
            step.assessmentResults = false;
            break;
          case "placement-error":
            step.errorMessage =
              step.errorMessageMap?.["placement-error"] &&
              step.errorMessageMap?.["placement-error"].length > 0
                ? step.errorMessageMap?.["placement-error"]
                : "Oops! You did not pass the assessment.";
            "Oops! You did not pass the assessment.";

            if (hasOnlyPlacement(step, "topicClinchers")) {
              step.errorMessage =
                "Oops! Your selected words must be in the first sentence and the last sentence of each body paragraph.";
            }
            step.assessmentResults = false;
            break;
          case "distribution-error":
            step.errorMessage =
              step.errorMessageMap?.["distribution-error"] &&
              step.errorMessageMap?.["distribution-error"].length > 0
                ? step.errorMessageMap?.["distribution-error"]
                : "Oops! You did not pass the assessment.";
            "Oops! You did not pass the assessment.";
            step.assessmentResults = false;
            break;
          case "duplicate-attribute-error":
            step.errorMessage =
              step.errorMessageMap?.["duplicate-attribute-error"] &&
              step.errorMessageMap?.["duplicate-attribute-error"].length > 0
                ? step.errorMessageMap?.["duplicate-attribute-error"]
                : "Oops! You did not pass the assessment.";
            "Oops! You did not pass the assessment.";
            step.assessmentResults = false;
            break;
          case "multiple-tokens-error":
            step.errorMessage =
              step.errorMessageMap?.["multiple-tokens-error"] &&
              step.errorMessageMap?.["multiple-tokens-error"].length > 0
                ? step.errorMessageMap?.["multiple-tokens-error"]
                : "For each selection, choose one word only.";
            "For each selection, choose one word only.";
            step.assessmentResults = false;
            break;
          case "unknown-error":
          default:
            step.errorMessage =
              step.errorMessageMap?.["unknown-error"] &&
              step.errorMessageMap?.["unknown-error"].length > 0
                ? step.errorMessageMap?.["unknown-error"]
                : "Oops! Something went wrong.";
            "Oops! Something went wrong.";
            step.assessmentResults = false;
        }

        response = false;
      }

      return response;
    };

    const setActiveStepIndex = (index: number) => {
      activeStepIndex.value = index;
    };

    const activeStep = computed(() => {
      if (activeStepIndex.value == undefined) return undefined;
      return allSteps.value[activeStepIndex.value];
    });

    const stepCriteriaAttributes = computed(() => {
      return selfAssessmentConfiguration.value.steps.map(
        (step) => step.criteriaAttribute
      );
    });

    const nextStep = () => {
      if (activeStepIndex.value == undefined) return;

      setActiveStepIndex(activeStepIndex.value + 1);
    };

    const bannedWordsInstances = ref<BannedWordInstance[]>([]);
    const bannedWordMatcher = computed(() => createBannedWordMatcher(bannedWords.value));

    // Match offsets update synchronously; drawing is coalesced by the renderer.
    const checkForBannedWords = (quillInstance?: any) => {
      const quill = quillInstance ?? wordProcessorStore.getQuillInstance();
      if (!quill?.getContents) {
        bannedWordsInstances.value = [];
        return;
      }
      // Also invalidate paragraph ranges after silent restore/import operations.
      quillContentTimestamp.value++;
      const text = getBannedWordText(quill.getContents().ops ?? []);
      const matches = bannedWordMatcher.value(text, bodyParagraphs.value.map((p) => p.data));
      if (JSON.stringify(matches) !== JSON.stringify(bannedWordsInstances.value)) {
        bannedWordsInstances.value = matches;
      }
    };

    const allSentences = computed(() => {
      const quill = wordProcessorStore.getQuillInstance();
      if (!quill) return [];

      // Track editor content changes so sentence ranges stay in sync while typing.
      quillContentTimestamp.value;
      const paragraphs = allParagraphs.value;

      let sentences = [];
      let index = 0;
      for (const paragraph of paragraphs) {
        const paragraphSentences = useParagraphSentences(
          quillDelta.value?.ops ?? revision.value?.studentSubmissionOps ?? [],
          quill,
          index
        );
        sentences.push(...paragraphSentences);
        index++;
      }
      return sentences;
    });

    const stepRelevantInstances = computed(() => {
      return (step: SelfAssessmentStep) => {
        if (revision.value == undefined) return [];

        if (revision.value.studentSubmissionOps == undefined) return [];

        const quill = wordProcessorStore.getQuillInstance();
        if (!quill) return [];

        var relevantInstances = useAttributeInstances(
          quill,
          revision.value.studentSubmissionOps,
          step.criteriaAttribute.attribute
        );

        if (step.criteriaInstance?.evaluationMethod == "paragraph") {
          const stepBodyParagraphs = useResolveBodyParagraphIndexes(
            stepParagraphPositions.value(step),
            bodyParagraphs.value
          );

          return relevantInstances.filter((instance) => {
            return stepBodyParagraphs.includes(instance.paragraphIndex ?? -1);
          });
        }
        return relevantInstances;
      };
    });

    watch(activeStepIndex, async () => {
      if (activeStepIndex.value == undefined) return;
      submittedDocument.value!.studentGuidedReviewStepIndex =
        activeStepIndex.value;
      await useSubmittedDocumentStore(documentId).saveDocument();
    });

    const resetSelection = () => {
      const selectedCriteriaAttributes = document.querySelectorAll(
        ".selected-criteria-attribute"
      );
      for (const selectedCriteriaAttribute of selectedCriteriaAttributes) {
        selectedCriteriaAttribute.classList.remove(
          "selected-criteria-attribute"
        );
      }

      const firstElements = document.querySelectorAll(".first");
      for (const firstElement of firstElements) {
        firstElement.classList.remove("first");
      }

      const lastElements = document.querySelectorAll(".last");
      for (const lastElement of lastElements) {
        lastElement.classList.remove("last");
      }
    };

    const applyStepSelection = () => {
      // Get the active step

      if (activeStep.value == undefined) return;

      // Get the criteria attribute of the active step
      const criteriaAttribute = activeStep.value?.criteriaAttribute;
      if (criteriaAttribute == undefined) return;

      for (const instance of relevantInstances.value) {
        const elements = document.querySelectorAll(
          `[${criteriaAttribute.attribute}="${instance.value}"]`
        );
        if (elements == undefined) continue;

        let index = 0;

        for (const element of elements) {
          element.classList.add("selected-criteria-attribute");

          if (index == 0) {
            element.classList.add("first");
          }
          if (index == elements.length - 1) {
            element.classList.add("last");
          }

          index++;
        }
      }
    };

    const relevantInstances = computed(() => {
      if (revision.value == undefined) return [];

      if (activeStep.value?.criteriaAttribute.attribute == undefined) return [];

      return stepRelevantInstances.value(activeStep.value);
    });

    return {
      activeStepIndex,
      selfAssessmentConfiguration,
      isRunningAssessment,
      assessStep,
      runAssessment,
      setActiveStepIndex,
      showBadge,
      activeStep,
      nextStep,
      setQuillEditor,
      stepCriteriaAttributes,
      isInitialized,
      initialize,
      stepExpectedInstances,
      headerSteps,
      hasRunAssessment,
      generalError,
      bannedWordsInstances,
      checkForBannedWords,
      allParagraphs,
      documentBlockAnalysis,
      documentBlocks,
      bodyBlocks,
      detectedBodyParagraphCount,
      allSteps,
      allSentences,
      bodyParagraphs,
      headerParagraphs,
      preCheckSteps,
      guidedReviewSteps,
      baseMessageSteps,
      bannedWords,
      stepParagraphPositions,
      quillDelta,
      quillContentTimestamp,
      stepRelevantInstances,
      resetSelection,
      relevantInstances,
      applyStepSelection,
    };
  });
